/**
 * OrderingQuiz Student Renderer
 * Student-style UI with drag-and-drop reorderable items, 3D shadows, and bright colors.
 * Uses useOrderingQuiz headless hook for logic and @dnd-kit for drag-and-drop.
 */

import { forwardRef, useCallback, useState } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '../../../lib/utils'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import type { OrderingQuizData, OrderingChoice } from '../hooks'
import { useOrderingQuiz } from '../hooks'
import { KatexText, SubmitButton, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import {
  ORDERING_DROP_ANIMATION,
  ORDERING_MOUSE_ACTIVATION_CONSTRAINT,
  ORDERING_TOUCH_ACTIVATION_CONSTRAINT,
  reorderOrderingChoices,
} from '../ordering-dnd'

// ──────────────────────────────────────────────
// Axis restriction modifiers
// ──────────────────────────────────────────────

const restrictToHorizontal = ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({
  ...transform,
  y: 0,
})

const restrictToVertical = ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({
  ...transform,
  x: 0,
})

// ──────────────────────────────────────────────
// SortableItem Sub-component (Student style)
// ──────────────────────────────────────────────

interface OrderingStudentCardProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  answer: OrderingChoice
  index: number
  isCorrect?: boolean | null
  isSolved: boolean
  isDragging: boolean
  isOverlay?: boolean
}

const OrderingStudentCard = forwardRef<HTMLDivElement, OrderingStudentCardProps>(
  function OrderingStudentCard(
    {
      answer,
      index,
      isCorrect,
      isSolved,
      isDragging,
      isOverlay = false,
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          "group relative flex items-center justify-center w-full max-w-[400px] h-[80px] mx-auto md:h-[176px] px-4 py-3 border-2 rounded-3xl bg-white transition-all",
          "touch-manipulation select-none",
          "[box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.10)]",
          isDragging && "border-lc-lightblue-900 bg-lc-lightblue-100 text-lc-lightblue-900 [box-shadow:0px_8px_16px_0px_rgba(0,0,0,0.20)] z-10",
          isDragging && !isOverlay && "opacity-30",
          !isDragging && !isSolved && "border-lc-gray-400 text-lc-gray-800 hover:border-lc-lightblue-900 hover:text-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.30)]",
          isSolved && isCorrect === true && "border-lc-mint-900 bg-lc-mint-100 text-lc-mint-dark [box-shadow:unset]",
          isSolved && isCorrect === false && "border-lc-red-900 bg-lc-red-100 text-lc-red-900 [box-shadow:unset]",
          isSolved && "pointer-events-none",
          className,
        )}
        {...props}
      >
        {/* Number badge */}
        <div className={cn(
          "absolute left-1/2 -translate-x-1/2 -top-4 flex items-center justify-center rounded-full font-bold transition-all",
          isDragging && "w-20 h-8 bg-lc-lightblue-900 text-white border-none",
          !isDragging && !isSolved && "w-8 h-8 border-2 border-lc-gray-400 bg-[#f5f5f5] text-[#999] group-hover:bg-lc-lightblue-200 group-hover:border-lc-lightblue-900 group-hover:text-lc-lightblue-900",
          isSolved && isCorrect === true && "w-8 h-8 bg-lc-mint-900 text-white border-none",
          isSolved && isCorrect === false && "w-8 h-8 bg-lc-red-900 text-white border-none"
        )}>
          {index + 1}
        </div>

        {/* Content */}
        {answer.image && (
          <img
            src={answer.image.signedUrl || answer.image.url}
            alt={`item-${index}`}
            className="max-h-24 w-auto object-contain"
          />
        )}
        {answer.answer && (
          <span className="text-[20px] font-medium text-center">
            <KatexText text={answer.answer} />
          </span>
        )}
      </div>
    )
  },
)

interface SortableItemProps {
  id: string
  answer: OrderingChoice
  index: number
  isCorrect?: boolean | null
  isSolved: boolean
}

function SortableItem({ id, answer, index, isCorrect, isSolved }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : ('auto' as const),
  }

  return (
    <OrderingStudentCard
      ref={setNodeRef}
      style={style}
      answer={answer}
      index={index}
      isCorrect={isCorrect}
      isSolved={isSolved}
      isDragging={isDragging}
      {...attributes}
      {...listeners}
    />
  )
}

// ──────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────

interface OrderingStudentProps {
  quizId: string | number
  quiz: OrderingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/문제 이미지만 흐린다. */
  dimChoiceText?: boolean
}

export function OrderingStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: OrderingStudentProps) {
  const { t } = useTranslation('quiz')
  const [activeId, setActiveId] = useState<string | null>(null)

  const {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit,
  } = useOrderingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // DnD sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: ORDERING_MOUSE_ACTIVATION_CONSTRAINT,
    }),
    useSensor(TouchSensor, {
      activationConstraint: ORDERING_TOUCH_ACTIVATION_CONSTRAINT,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }, [])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  // Drag end handler using hook's setOrderedChoices + arrayMove
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    if (!over || active.id === over.id) return

    setOrderedChoices(prev => {
      return reorderOrderingChoices(prev, String(active.id), String(over.id))
    })
  }, [setOrderedChoices])

  const displayChoices = orderedChoices
  const activeIndex = activeId === null
    ? -1
    : displayChoices.findIndex(choice => choice.key === activeId)
  const activeChoice = activeIndex >= 0 ? displayChoices[activeIndex] : null

  // Arrow color
  const getArrowColor = () => {
    if (!isSolved) return 'text-lc-lightblue-900'
    if (solveResult?.correct || shouldShowCorrectAnswers) return 'text-lc-mint-900'
    return 'text-lc-red-900'
  }

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.ordering.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimMedia={dimChoiceText}
        problem={quiz.problem && (
          <div className="text-xl leading-relaxed text-gray-900">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      {/* Drag instruction */}
      {!isSolved && !isViewSolved && (
        <p className="text-base text-gray-500">
          {t('types.ordering.dragInstruction')}
        </p>
      )}

      {/* DnD sortable list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        modifiers={isVertical ? [restrictToVertical] : [restrictToHorizontal]}
      >
        <SortableContext
          items={displayChoices.map(c => c.key)}
          strategy={isVertical ? verticalListSortingStrategy : horizontalListSortingStrategy}
        >
          <div className={cn(
            isVertical
              ? "flex flex-col items-center justify-start space-y-14 p-2"
              : "flex flex-row items-center justify-between space-x-8 p-2"
          )}>
            {displayChoices.map((choice, index) => (
              <div
                key={choice.key}
                className="relative"
                style={!isVertical ? { width: `${100 / displayChoices.length}%` } : undefined}
              >
                {/* Arrow between items */}
                {index > 0 && (
                  <ArrowRight
                    strokeWidth={3}
                    className={cn(
                      "absolute w-6 h-6",
                      getArrowColor(),
                      isVertical
                        ? "left-1/2 -translate-x-1/2 -top-9 rotate-90"
                        : "-left-5 top-1/2 -translate-y-1/2"
                    )}
                  />
                )}
                <SortableItem
                  id={choice.key}
                  answer={choice}
                  index={index}
                  isCorrect={isSolved ? (shouldShowCorrectAnswers || (solveResult?.correct ?? false)) : null}
                  isSolved={isSolved}
                />
              </div>
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={ORDERING_DROP_ANIMATION}>
          {activeChoice ? (
            <OrderingStudentCard
              answer={activeChoice}
              index={activeIndex}
              isSolved={isSolved}
              isDragging
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? t('common.checkAnswer')}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
